import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

import { config } from './config';

export interface Session {
  idToken: string;
  accountId: string;
  // 'operator' is the internal ISO admin-console principal (DECISION-059) — a
  // third account type the shell routes to its own console, off the two rails.
  accountType: 'hotel' | 'supplier' | 'operator';
  name: string;
}

// Session-restore state is never gated on a UX convenience flag: the session
// always lands in sessionStorage. (localStorage "remember" can come later —
// the desk rule is a 16h refresh TTL, not a 90-day one.)
const KEY = 'iso.session';

export function currentSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    const claims = JSON.parse(atob(s.idToken.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    if (claims.exp * 1000 < Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

function store(idToken: string): Session {
  const claims = JSON.parse(atob(idToken.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
  const session: Session = {
    idToken,
    accountId: claims['custom:accountId'],
    accountType: claims['custom:accountType'],
    name: claims.email ?? claims.phone_number ?? 'you',
  };
  sessionStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

export function signOut(): void {
  sessionStorage.removeItem(KEY);
}

// The remembered account id. Written to localStorage only when "remember" is
// ticked, but ALWAYS to sessionStorage — session restore is never gated on a
// UX convenience flag.
const ACCOUNT_KEY = 'iso.accountId';

export function rememberedAccountId(): string {
  return sessionStorage.getItem(ACCOUNT_KEY) ?? localStorage.getItem(ACCOUNT_KEY) ?? '';
}

export function storeAccountId(accountId: string, remember: boolean): void {
  sessionStorage.setItem(ACCOUNT_KEY, accountId);
  if (remember) localStorage.setItem(ACCOUNT_KEY, accountId);
  else localStorage.removeItem(ACCOUNT_KEY);
}

// The typed account id and the token's tenant must agree — a credential from
// another account signing in "under" this one is an error, not a curiosity.
export function assertAccountMatch(session: Session, accountId: string): Session {
  if (session.accountId !== accountId) {
    signOut();
    throw new Error('Those credentials belong to a different account.');
  }
  return session;
}

// Hotel staff: SRP against the pool. The password never crosses the wire.
export function signInHotel(email: string, password: string): Promise<Session> {
  const c = config();
  const pool = new CognitoUserPool({ UserPoolId: c.userPoolId, ClientId: c.platformClientId });
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: (result) => resolve(store(result.getIdToken().getJwtToken())),
      onFailure: () => reject(new Error('That did not work. Check the email and password.')),
      newPasswordRequired: () => reject(new Error('This account needs a new password set by an admin.')),
    });
  });
}

// Suppliers: the SMS-code flow through the API. No password exists.
export async function requestCode(phone: string): Promise<string> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/code`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ phone, surface: 'platform' }),
  });
  if (!res.ok) throw new Error('Could not send a code. Check the number.');
  const body = await res.json();
  return body.challengeId as string;
}

export async function verifyCode(challengeId: string, code: string): Promise<Session> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ challengeId, code }),
  });
  if (!res.ok) throw new Error('That code is wrong or has expired.');
  const body = await res.json();
  return store(body.idToken as string);
}

// The magic link: first-class for shared desk machines (canon 09, Entry).
// Request always looks the same from the outside — existence is never
// disclosed — and the emailed token redeems on any browser.
export async function requestMagicLink(email: string): Promise<void> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/code`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, surface: 'platform' }),
  });
  if (!res.ok) throw new Error('Could not send the link. Check the email address.');
}

export async function redeemLink(token: string): Promise<Session> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ linkToken: token }),
  });
  if (!res.ok) throw new Error('This link has expired or was already used.');
  const body = await res.json();
  return store(body.idToken as string);
}
